import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Divider, IconButton, MenuItem, Select,
  FormControl, InputLabel, TextField, CircularProgress, Checkbox,
  Chip, Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import api from '../../services/api';
import { toast } from 'react-toastify';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: 12,
    minWidth: 560,
    maxWidth: 720,
  },
  dialogTitleRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 8,
  },
  dropZone: {
    border: '2px dashed #d0d0d0',
    borderRadius: 12,
    padding: '36px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
    '&:hover': { borderColor: '#1a1a1a', background: '#fafafa' },
  },
  dropText: { color: '#9b9b9b', fontSize: '0.85rem', marginTop: 8 },
  fileChip: { margin: 2 },
  sectionLabel: {
    fontSize: '0.7rem',
    color: '#9b9b9b',
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: '0.05em',
    marginTop: 12,
    marginBottom: 6,
  },
  summaryRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  summaryPill: {
    fontSize: '0.72rem',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 20,
    background: '#f0f0f0',
    color: '#444',
  },
  configRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  inputField: {
    '& .MuiOutlinedInput-root': { borderRadius: 8, fontSize: '0.85rem' },
    '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  },
  warningBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: '0.78rem',
    color: '#8a6d00',
    margin: '8px 0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.78rem',
  },
  th: {
    textAlign: 'left',
    padding: '6px 8px',
    color: '#9b9b9b',
    fontWeight: 700,
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    borderBottom: '1px solid #ebebeb',
    position: 'sticky',
    top: 0,
    background: '#fff',
  },
  td: {
    padding: '5px 8px',
    borderBottom: '1px solid #f3f3f3',
    color: '#333',
  },
  tableWrap: {
    maxHeight: 320,
    overflowY: 'auto',
    border: '1px solid #ebebeb',
    borderRadius: 8,
    marginTop: 6,
  },
  badgeInvalid: {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: '#c62828',
    background: '#ffebee',
    padding: '1px 6px',
    borderRadius: 10,
  },
  badgeDup: {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: '#ef6c00',
    background: '#fff3e0',
    padding: '1px 6px',
    borderRadius: 10,
  },
  badgeExists: {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: '#1565c0',
    background: '#e3f2fd',
    padding: '1px 6px',
    borderRadius: 10,
  },
  saveBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    minWidth: 120,
  },
  cancelBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontSize: '0.85rem',
    color: '#6b6b6b',
  },
  doneBox: {
    textAlign: 'center',
    padding: '24px 12px',
  },
}));

const KanbanImportLeadsModal = ({ open, onClose, lanes = [], connections = [], onImported }) => {
  const classes = useStyles();

  const [step, setStep] = useState('upload'); // upload | preview | done
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);

  const [laneId, setLaneId] = useState('lane0');
  const [whatsappId, setWhatsappId] = useState('');
  const [interval, setIntervalSec] = useState(40);
  const [result, setResult] = useState(null);

  // Tags normais (kanban=0) disponíveis + tag escolhida p/ aplicar a todos os leads
  const [availableTags, setAvailableTags] = useState([]);
  const [tagId, setTagId] = useState('');

  const laneOptions = lanes; // inclui "Novos Leads" (lane0)

  // Ao abrir: pré-seleciona a conexão padrão da empresa e carrega as tags
  useEffect(() => {
    if (!open) return;
    const def = connections.find(c => c.isDefault);
    if (def) setWhatsappId(prev => prev || def.id);

    const loadTags = async () => {
      try {
        const { data } = await api.get('/tags/list', { params: { kanban: 0 } });
        setAvailableTags(data || []);
      } catch {
        setAvailableTags([]);
      }
    };
    loadTags();
  }, [open, connections]);

  const reset = () => {
    setStep('upload');
    setFiles([]);
    setLeads([]);
    setSummary(null);
    setLaneId('lane0');
    setWhatsappId('');
    setIntervalSec(40);
    setTagId('');
    setResult(null);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFiles = e => {
    const picked = Array.from(e.target.files || []);
    setFiles(picked);
  };

  const handleAnalyze = async () => {
    if (files.length === 0) {
      toast.error('Selecione ao menos um arquivo CSV');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const { data } = await api.post('/meta-leads/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const withCheck = (data.leads || []).map(l => ({
        ...l,
        _checked: l.valid && !l.duplicateInBatch && !l.alreadyExists,
      }));
      setLeads(withCheck);
      setSummary({
        total: data.total,
        validos: data.validos,
        invalidos: data.invalidos,
        duplicados: data.duplicados,
        jaExistem: data.jaExistem,
      });
      setStep('preview');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Erro ao analisar os arquivos';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleLead = idx => {
    setLeads(prev => prev.map((l, i) => (i === idx ? { ...l, _checked: !l._checked } : l)));
  };

  const toggleAll = checked => {
    setLeads(prev => prev.map(l => ({ ...l, _checked: checked })));
  };

  const selectedCount = leads.filter(l => l._checked).length;

  const handleImport = async () => {
    const chosen = leads.filter(l => l._checked && l.number);
    if (chosen.length === 0) {
      toast.error('Nenhum lead selecionado');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        leads: chosen.map(l => ({
          name: l.name,
          number: l.number,
          email: l.email,
          extraInfo: l.extraInfo,
        })),
        stageId: laneId && laneId !== 'lane0' ? laneId : null,
        whatsappId: whatsappId || null,
        intervalSeconds: Number(interval) || 40,
        tagId: tagId || null,
      };
      const { data } = await api.post('/meta-leads/import', payload);
      setResult(data);
      setStep('done');
      onImported && onImported();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Erro ao importar';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isNovosLeads = !laneId || laneId === 'lane0';

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.dialogPaper }}>
      <DialogTitle disableTypography className={classes.dialogTitleRoot}>
        <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 700 }}>
          Importar Leads do Meta
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent>
        {/* STEP 1 — UPLOAD */}
        {step === 'upload' && (
          <>
            <label htmlFor="meta-leads-file" className={classes.dropZone} style={{ display: 'block' }}>
              <CloudUploadIcon style={{ fontSize: 40, color: '#c0c0c0' }} />
              <Typography style={{ fontWeight: 600, fontSize: '0.9rem', color: '#444' }}>
                Clique para selecionar os arquivos
              </Typography>
              <Typography className={classes.dropText}>
                Planilhas .csv exportadas do Meta (pode selecionar várias)
              </Typography>
              <input
                id="meta-leads-file"
                type="file"
                accept=".csv,text/csv"
                multiple
                hidden
                onChange={handleFiles}
              />
            </label>
            {files.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {files.map((f, i) => (
                  <Chip key={i} label={f.name} size="small" className={classes.fileChip} />
                ))}
              </div>
            )}
          </>
        )}

        {/* STEP 2 — PREVIEW */}
        {step === 'preview' && summary && (
          <>
            <div className={classes.summaryRow}>
              <span className={classes.summaryPill}>{summary.total} no total</span>
              <span className={classes.summaryPill} style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                {summary.validos} prontos
              </span>
              {summary.invalidos > 0 && (
                <span className={classes.summaryPill} style={{ background: '#ffebee', color: '#c62828' }}>
                  {summary.invalidos} inválidos
                </span>
              )}
              {summary.duplicados > 0 && (
                <span className={classes.summaryPill} style={{ background: '#fff3e0', color: '#ef6c00' }}>
                  {summary.duplicados} duplicados
                </span>
              )}
              {summary.jaExistem > 0 && (
                <span className={classes.summaryPill} style={{ background: '#e3f2fd', color: '#1565c0' }}>
                  {summary.jaExistem} já no CRM
                </span>
              )}
            </div>

            <Typography className={classes.sectionLabel}>Configurações da importação</Typography>
            <div className={classes.configRow}>
              <FormControl variant="outlined" size="small" className={classes.inputField} style={{ minWidth: 180 }}>
                <InputLabel>Coluna destino</InputLabel>
                <Select value={laneId} onChange={e => setLaneId(e.target.value)} label="Coluna destino">
                  {laneOptions.map(lane => (
                    <MenuItem key={lane.id} value={lane.id}>{lane.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" className={classes.inputField} style={{ minWidth: 170 }}>
                <InputLabel>Conexão</InputLabel>
                <Select value={whatsappId} onChange={e => setWhatsappId(e.target.value)} label="Conexão">
                  <MenuItem value=""><em>Padrão da empresa</em></MenuItem>
                  {connections.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}{c.isDefault ? ' (padrão)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" className={classes.inputField} style={{ minWidth: 160 }}>
                <InputLabel>Tag</InputLabel>
                <Select value={tagId} onChange={e => setTagId(e.target.value)} label="Tag">
                  <MenuItem value=""><em>Sem tag</em></MenuItem>
                  {availableTags.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Intervalo (s)"
                type="number"
                value={interval}
                onChange={e => setIntervalSec(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.inputField}
                style={{ width: 110 }}
                inputProps={{ min: 5 }}
                helperText="entre leads"
              />
            </div>

            <div className={classes.warningBox}>
              <WarningRoundedIcon style={{ fontSize: 18 }} />
              {isNovosLeads
                ? `Os leads vão para "Novos Leads" e a automação será disparada para cada um, respeitando o intervalo de ${interval}s. Tempo estimado: ~${Math.ceil((selectedCount * (Number(interval) || 40)) / 60)} min.`
                : `Cada lead entra na coluna selecionada e dispara a automação respeitando o intervalo de ${interval}s. Tempo estimado: ~${Math.ceil((selectedCount * (Number(interval) || 40)) / 60)} min.`}
            </div>

            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th} style={{ width: 36 }}>
                      <Checkbox
                        size="small"
                        checked={selectedCount === leads.length && leads.length > 0}
                        indeterminate={selectedCount > 0 && selectedCount < leads.length}
                        onChange={e => toggleAll(e.target.checked)}
                      />
                    </th>
                    <th className={classes.th}>Nome</th>
                    <th className={classes.th}>Telefone</th>
                    <th className={classes.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, idx) => (
                    <tr key={idx} style={{ opacity: l._checked ? 1 : 0.5 }}>
                      <td className={classes.td}>
                        <Checkbox size="small" checked={!!l._checked} onChange={() => toggleLead(idx)} />
                      </td>
                      <td className={classes.td}>{l.name}</td>
                      <td className={classes.td}>
                        {l.valid ? l.number : (
                          <Tooltip title={l.rawNumber || ''}>
                            <span>{l.rawNumber || '—'}</span>
                          </Tooltip>
                        )}
                      </td>
                      <td className={classes.td}>
                        {!l.valid && <span className={classes.badgeInvalid}>inválido</span>}
                        {l.duplicateInBatch && <span className={classes.badgeDup} style={{ marginLeft: 4 }}>duplicado</span>}
                        {l.alreadyExists && <span className={classes.badgeExists} style={{ marginLeft: 4 }}>já no CRM</span>}
                        {l.valid && !l.duplicateInBatch && !l.alreadyExists && (
                          <span style={{ color: '#2e7d32', fontSize: '0.7rem', fontWeight: 600 }}>ok</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* STEP 3 — DONE */}
        {step === 'done' && result && (
          <div className={classes.doneBox}>
            <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#2e7d32' }}>
              {result.enqueued} leads na fila de importação
            </Typography>
            <Typography style={{ color: '#6b6b6b', fontSize: '0.85rem', marginTop: 8 }}>
              Os leads serão criados aos poucos, um a cada {result.intervalSeconds}s, para não
              sobrecarregar a automação. Tempo estimado: ~{result.estimatedMinutes} min.
            </Typography>
            <Typography style={{ color: '#9b9b9b', fontSize: '0.78rem', marginTop: 8 }}>
              Você pode fechar esta janela — eles vão aparecendo no kanban automaticamente.
            </Typography>
          </div>
        )}
      </DialogContent>

      <Divider />
      <DialogActions style={{ padding: '12px 24px' }}>
        {step === 'upload' && (
          <>
            <Button onClick={handleClose} className={classes.cancelBtn}>Cancelar</Button>
            <Button
              onClick={handleAnalyze}
              color="primary"
              variant="contained"
              className={classes.saveBtn}
              disabled={loading || files.length === 0}
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : 'Analisar'}
            </Button>
          </>
        )}
        {step === 'preview' && (
          <>
            <Button onClick={() => setStep('upload')} className={classes.cancelBtn}>Voltar</Button>
            <Button
              onClick={handleImport}
              color="primary"
              variant="contained"
              className={classes.saveBtn}
              disabled={loading || selectedCount === 0}
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : `Importar ${selectedCount}`}
            </Button>
          </>
        )}
        {step === 'done' && (
          <Button onClick={handleClose} color="primary" variant="contained" className={classes.saveBtn}>
            Concluir
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default KanbanImportLeadsModal;
